import React, { useEffect, useRef, useState } from 'react'
import { api } from 'components/api'
import { surveyPath } from 'components/routes/api'
import { SurveyResponseForm } from 'components/surveys/responses/SurveyResponseForm'
import type { SurveyAnswer } from 'components/surveys/responses/surveyResponses'
import { toApiSurveyAnswer } from 'components/surveys/responses/services/surveyResponseData'
import type { PersistedQuestion } from 'components/surveys/questionTypes'
import type { TargetCriteria } from 'components/surveys/builder/surveyDraft'
import { surveyTargetMismatch, type ReviewTargetCriteria } from './surveyTargetMatch'
import SurveySearch, { SurveyResult } from './SurveySearch'

interface FullSurvey {
  survey_slug: string
  name: string
  questions: PersistedQuestion[]
  targets: TargetCriteria[]
}

const toId = (value: unknown): number | null => (value == null || value === '' ? null : Number(value))

const currentReviewCriteria = (agencyId?: number | null): ReviewTargetCriteria => ({
  supplier_id: toId($('#chosen-supplier-data').data('supplier')),
  contract_id: toId($('#review_contract_id').val()),
  contract_vehicle_id: toId($('#review_contract_vehicle_id').val()),
  agency_id: agencyId ?? null
})

interface SurveySelectorProps {
  agencyId?: number | null
}

type SurveyLoadState =
  | { status: 'idle', selectedId: null, survey: null }
  | { status: 'loading', selectedId: number, survey: null }
  | { status: 'loaded', selectedId: number, survey: FullSurvey }
  | { status: 'error', selectedId: null, survey: null }

const IDLE: SurveyLoadState = { status: 'idle', selectedId: null, survey: null }

const SurveySelector: React.FC<SurveySelectorProps> = ({ agencyId }) => {
  const [loadState, setLoadState] = useState<SurveyLoadState>(IDLE)
  const [answers, setAnswers] = useState<SurveyAnswer[]>([])
  const [mismatch, setMismatch] = useState(false)
  const latestSelectionId = useRef(0)

  const { selectedId, survey } = loadState

  const handleSelect = (selected: SurveyResult): void => {
    const selectionId = ++latestSelectionId.current
    setAnswers([])
    setLoadState({ status: 'loading', selectedId: selected.id, survey: null })
    api.get<unknown, FullSurvey>({ url: surveyPath(selected.slug) })
      .then((response) => {
        if (selectionId !== latestSelectionId.current) return
        setLoadState({ status: 'loaded', selectedId: selected.id, survey: response.data })
      })
      .catch(() => {
        if (selectionId !== latestSelectionId.current) return
        setLoadState({ status: 'error', selectedId: null, survey: null })
      })
  }

  useEffect(() => {
    $('#survey_response_survey_id').val(selectedId ?? '').change()
  }, [selectedId])

  useEffect(() => {
    $('#survey_response_answers').val(JSON.stringify(answers.map(toApiSurveyAnswer))).change()
  }, [answers])

  useEffect(() => {
    if (survey == null) {
      setMismatch(false)
      return
    }

    const recompute = (): void => {
      setMismatch(surveyTargetMismatch(survey.targets, currentReviewCriteria(agencyId)))
    }

    recompute()

    const supplierDataEl = document.getElementById('chosen-supplier-data')
    const observer = new MutationObserver(recompute)
    if (supplierDataEl != null) {
      observer.observe(supplierDataEl, { attributes: true })
    }

    $('#review_contract_id').on('change', recompute)
    $('#review_contract_vehicle_id').on('change', recompute)

    return () => {
      observer.disconnect()
      $('#review_contract_id').off('change', recompute)
      $('#review_contract_vehicle_id').off('change', recompute)
    }
  }, [survey, agencyId])

  return (
    <>
      <SurveySearch onSelect={handleSelect} />
      {loadState.status === 'loading' && <p>Loading survey…</p>}
      {loadState.status === 'error' && <p>Could not load this survey. Please try selecting it again.</p>}
      {survey != null && (
        <>
          {!mismatch && <h3>{survey.name}</h3>}
          <div className={mismatch ? 'callout alert' : undefined}>
            {mismatch && (
              <>
                <h3>{survey.name}</h3>
                <p role='alert' id='survey-target-mismatch-warning'>
                  This survey's targeting criteria may not match this review. You can still submit it.
                </p>
              </>
            )}
            <SurveyResponseForm
              key={survey.survey_slug}
              questions={survey.questions}
              onSubmit={() => {}}
              onResponseChange={(_questionId, _value, allAnswers) => { setAnswers(allAnswers) }}
              hideSubmitButton
            />
          </div>
        </>
      )}
    </>
  )
}

export default SurveySelector
